import {navigate} from 'gatsby';
import useParams from '@src/Hooks/useParams';
import {useEffect, useState} from 'react';
import {WatchPageRouteParams} from '@src/Navigation/types/watchPageRouteParams.type';
import {useLocation} from '@reach/router';
import {
  IVodItemFragmentFragment,
  useVodItemByIdQuery,
} from '@src/Api/Graphql/gql/types.generated';

export type VideoPlayerParams = {
  umbracoId?: string; // id décodé depuis ctx ou id
  vimeoId?: string;
  byteplusId?: string;
  youtubeId?: string;
  params: Record<string, any>; // tous les params bruts
  location: ReturnType<typeof useLocation>;
  title: string;
  isFullscreen?: boolean;
  paramsReady?: boolean;
};

export type WithContext<T> = T & {
  ctx?: string;
};

function encodeUuidToNumber(uuid: string): string {
  const hex = uuid.replace(/-/g, '');
  return BigInt('0x' + hex).toString(10);
}

function decodeNumberToUuid(num: string): string {
  let hex = BigInt(num).toString(16).padStart(32, '0');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export default function useVideoPlayerParamsNumeric(): VideoPlayerParams {
  const params = useParams<WithContext<WatchPageRouteParams>>();
  const location = useLocation();
  const [vodItem, setVodItem] = useState<IVodItemFragmentFragment | null>(null);
  const [paramsReady, setParamsReady] = useState(false);

  let umbracoId: string | undefined;
  if (params.ctx && typeof params.ctx === 'string') {
    umbracoId = decodeNumberToUuid(params.ctx);
  } else if (params.id && typeof params.id === 'string') {
    const ctx = encodeUuidToNumber(params.id);
    const basePath = location.pathname.replace(/\/+$/, '');
    let url = `${basePath}/${ctx}`;
    url = url.replace(/\/+$/, '');
    navigate(url, {replace: true, state: {params}});
    umbracoId = params.id;
  }

  // On fetch uniquement si on a umbracoId et pas les autres ids
  const shouldFetchUmbraco =
    !!umbracoId && !params.byteplusId && !params.youtubeId && !params.vimeoId;

  const {data} = useVodItemByIdQuery({
    variables: {id: umbracoId},
    errorPolicy: 'all',
    skip: !shouldFetchUmbraco,
    fetchPolicy: 'cache-first',
  });

  useEffect(() => {
    if (!shouldFetchUmbraco || vodItem) return;

    setVodItem(data?.vODItem || null);
  }, [shouldFetchUmbraco, data]);

  useEffect(() => {
    const {pathname, search, hash} = window.location;
    if (pathname.length > 1 && pathname.match(/\/$/)) {
      const cleanPath = pathname.replace(/\/$/, '');
      const newUrl = cleanPath + search + hash;
      if (window.location.href !== window.location.origin + newUrl) {
        navigate(newUrl, {replace: true, state: {params}});
      }
    }
  }, []);

  // On enrichit les valeurs si on a le résultat du fetch
  let byteplusId = params.byteplusId;
  let youtubeId = params.youtubeId;
  let vimeoId = params.vimeoId;
  let title = params.title;

  // Pour les cours, on utilise les paramètres extraits au lieu du fetch GraphQL
  if (shouldFetchUmbraco && !vodItem) {
    // Si on doit fetch mais que vodItem n'existe pas (cas d'un Course),
    // on considère que paramsReady est true si on a umbracoId
    // Les vrais paramètres seront extraits par extractVideoParamsFromUmbraco
    useEffect(() => {
      setParamsReady(Boolean(umbracoId));
    }, [umbracoId]);
  } else {
    useEffect(() => {
      // paramsReady = true si on a les ids nécessaires
      const paramsReady = Boolean(
        (shouldFetchUmbraco &&
          vodItem &&
          (vodItem.byteplusVideoID ||
            vodItem.youtubeId ||
            vodItem.liveEventId)) ||
          (!shouldFetchUmbraco &&
            (byteplusId || youtubeId || vimeoId || umbracoId)),
      );
      setParamsReady(paramsReady);
    }, [
      shouldFetchUmbraco,
      vodItem,
      byteplusId,
      youtubeId,
      vimeoId,
      umbracoId,
    ]);
  }

  if (shouldFetchUmbraco && vodItem) {
    const item = vodItem;
    byteplusId = item.byteplusVideoID ?? byteplusId;
    youtubeId = item.youtubeId ?? youtubeId;
    vimeoId = item.liveEventId ? '' + item.liveEventId : vimeoId;
    title = item.title || title;
  }

  return {
    umbracoId,
    vimeoId,
    byteplusId,
    youtubeId,
    title,
    params,
    location,
    isFullscreen:
      typeof params.isFullscreen === 'undefined' ? true : params.isFullscreen,
    paramsReady,
  };
}
